// ============================================================================
// CONFIGURAÇÕES DA PLATAFORMA - DASHBOARD ADMIN
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/ConfiguracoesPlataforma.tsx
// UPDATED: 2026-03-07 — APIs reais conectadas
//
// FIXES:
//   [1] Salvamento real → localStorage (persistência local) + stub PATCH /admin/settings
//   [2] Saúde do sistema → GET /admin/system-health (REAL)
//   [3] Broadcast → POST /admin/broadcast-notification (REAL)
//   [4] Audit log → GET /admin/audit-log (REAL) na aba "Avançado"
//   [5] Loading states em todas as abas
//   [6] handleSalvar conectado — não usa mais alert() simples
//   [7] Nova aba "Sistema" com dados reais de health + audit log
//   [8] Tabs refinadas com ícones corretos
//
// ⚠️ BACKEND PENDENTE: GET/PATCH /admin/settings não existe.
//    Configurações ficam em localStorage até criação do endpoint.
//    Adicionar ao admin.controller.ts:
//      GET  /admin/settings   → lista todas as configurações
//      PATCH /admin/settings  → atualiza configurações em lote
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Save, AlertCircle, Globe, Zap, Lock, Mail,
  Download, CheckCircle, Loader2, Activity, RefreshCw, Bell,
  Shield, Database, Server, Cpu, MemoryStick, Clock
} from 'lucide-react';

import { API_BASE, authH } from '../../utils/apiConfig';

const SETTINGS_KEY = 'orus_platform_settings';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Configuracao {
  id: string;
  titulo: string;
  descricao: string;
  tipo: 'texto' | 'email' | 'numero' | 'toggle' | 'select';
  valor: string | boolean;
  opcoes?: string[];
  requerido: boolean;
  sensivel?: boolean;
}

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down';
  uptime: number;
  database: { status: string; latency: number };
  memory: { used: number; total: number };
  cpu: { usage: number };
  services: Array<{ name: string; status: string }>;
}

interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  adminId: string;
  createdAt: string;
}

// ─── Configurações padrão ────────────────────────────────────────────────────
const CONFIG_DEFAULTS: Configuracao[] = [
  // Geral
  { id: 'nome-plataforma', titulo: 'Nome da Plataforma', descricao: 'Nome principal exibido em toda a plataforma', tipo: 'texto', valor: 'Federação de Beleza', requerido: true },
  { id: 'email-suporte', titulo: 'E-mail de Suporte', descricao: 'E-mail principal para contato', tipo: 'email', valor: 'suporte@federacao.com.br', requerido: true },
  { id: 'telefone-suporte', titulo: 'Telefone de Suporte', descricao: 'Número de WhatsApp para suporte', tipo: 'texto', valor: '+55 11 98765-4321', requerido: true },
  { id: 'idioma', titulo: 'Idioma Padrão', descricao: 'Idioma padrão da plataforma', tipo: 'select', valor: 'pt-BR', opcoes: ['pt-BR', 'en-US', 'es-ES'], requerido: true },
  { id: 'timezone', titulo: 'Fuso Horário', descricao: 'Fuso horário do servidor', tipo: 'select', valor: 'America/Sao_Paulo', opcoes: ['America/Sao_Paulo', 'America/Manaus', 'America/Fortaleza', 'UTC'], requerido: true },
  // Integrações
  { id: 'stripe-key', titulo: 'Chave API Stripe', descricao: 'Chave secreta para pagamentos', tipo: 'texto', valor: 'sk_live_****', requerido: true, sensivel: true },
  { id: 'whatsapp-api', titulo: 'API WhatsApp', descricao: 'Token da API WhatsApp Business', tipo: 'texto', valor: 'whatsapp_token_****', requerido: false, sensivel: true },
  { id: 'smtp-host', titulo: 'SMTP Host', descricao: 'Servidor de envio de e-mails', tipo: 'texto', valor: 'smtp.sendgrid.net', requerido: false },
  { id: 'smtp-port', titulo: 'SMTP Porta', descricao: 'Porta do servidor SMTP', tipo: 'numero', valor: '587', requerido: false },
  // Segurança
  { id: 'autenticacao-2fa', titulo: 'Autenticação 2FA', descricao: 'Autenticação de dois fatores para admins', tipo: 'toggle', valor: true, requerido: false },
  { id: 'ssl-obrigatorio', titulo: 'SSL Obrigatório', descricao: 'Forçar HTTPS em toda a plataforma', tipo: 'toggle', valor: true, requerido: false },
  { id: 'tentativas-login', titulo: 'Máx. Tentativas de Login', descricao: 'Bloquear após N tentativas falhas', tipo: 'numero', valor: '5', requerido: false },
  { id: 'expirar-sessao', titulo: 'Expirar Sessão (min)', descricao: 'Minutos de inatividade até logout automático', tipo: 'numero', valor: '60', requerido: false },
];

// ─── Component ───────────────────────────────────────────────────────────────
const ConfiguracoesPlataforma: React.FC = () => {
  const [abaAtiva, setAbaAtiva] = useState<'geral' | 'integracao' | 'seguranca' | 'sistema'>('geral');
  const [configuracoes, setConfiguracoes] = useState<Configuracao[]>(CONFIG_DEFAULTS);
  const [salvo, setSalvo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [alterado, setAlterado] = useState(false);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [broadcast, setBroadcast] = useState({ titulo: '', mensagem: '', userType: 'all' });
  const [enviandoBroadcast, setEnviandoBroadcast] = useState(false);
  const [broadcastEnviado, setBroadcastEnviado] = useState(false);

  // Carregar configurações salvas do localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      if (saved) {
        const savedConfigs: Record<string, string | boolean> = JSON.parse(saved);
        setConfiguracoes(prev => prev.map(c => ({
          ...c,
          valor: savedConfigs[c.id] !== undefined ? savedConfigs[c.id] : c.valor,
        })));
      }
    } catch { /* usa defaults */ }
  }, []);

  // Fetch system health
  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const res = await fetch(`${API_BASE}/admin/system-health`, { headers: authH() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSystemHealth(data);
    } catch {
      setSystemHealth(null);
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  // Fetch audit log
  const fetchAudit = useCallback(async () => {
    setLoadingAudit(true);
    try {
      const res = await fetch(`${API_BASE}/admin/audit-log?limit=20`, { headers: authH() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAuditLog(data.entries ?? data.logs ?? []);
    } catch {
      setAuditLog([]);
    } finally {
      setLoadingAudit(false);
    }
  }, []);

  // Carregar dados da aba sistema quando selecionada
  useEffect(() => {
    if (abaAtiva === 'sistema') {
      fetchHealth();
      fetchAudit();
    }
  }, [abaAtiva, fetchHealth, fetchAudit]);

  // ─── Alterar configuração ──────────────────────────────────────────────────
  const handleAlterar = (id: string, valor: string | boolean) => {
    setConfiguracoes(prev => prev.map(c => c.id === id ? { ...c, valor } : c));
    setAlterado(true);
    setSalvo(false);
  };

  // ─── Salvar configurações ──────────────────────────────────────────────────
  const handleSalvar = async () => {
    setSalvando(true);
    try {
      // Persistir no localStorage
      const toSave = Object.fromEntries(configuracoes.map(c => [c.id, c.valor]));
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(toSave));

      // Tentar salvar no backend (endpoint pendente)
      try {
        await fetch(`${API_BASE}/admin/settings`, {
          method: 'PATCH', headers: authH(),
          body: JSON.stringify({ settings: toSave }),
        });
      } catch {
        // Backend pendente: ignora silenciosamente, salvo localmente
      }

      setSalvo(true);
      setAlterado(false);
      setTimeout(() => setSalvo(false), 3000);
    } finally {
      setSalvando(false);
    }
  };

  // ─── Broadcast ────────────────────────────────────────────────────────────
  const handleBroadcast = async () => {
    if (!broadcast.titulo.trim() || !broadcast.mensagem.trim()) {
      alert('Título e mensagem são obrigatórios');
      return;
    }
    setEnviandoBroadcast(true);
    try {
      const res = await fetch(`${API_BASE}/admin/broadcast-notification`, {
        method: 'POST', headers: authH(),
        body: JSON.stringify({
          title: broadcast.titulo,
          message: broadcast.mensagem,
          userType: broadcast.userType,
        }),
      });
      if (!res.ok) throw new Error();
      setBroadcastEnviado(true);
      setBroadcast({ titulo: '', mensagem: '', userType: 'all' });
      setTimeout(() => setBroadcastEnviado(false), 4000);
    } catch { alert('Erro ao enviar broadcast'); }
    finally { setEnviandoBroadcast(false); }
  };

  // ─── Backup ───────────────────────────────────────────────────────────────
  const handleBackup = () => {
    const data = {
      exportedAt: new Date().toISOString(),
      settings: Object.fromEntries(configuracoes.map(c => [c.id, c.valor])),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `backup_settings_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  // ─── Configs por aba ──────────────────────────────────────────────────────
  const configPorAba: Record<string, string[]> = {
    geral: ['nome-plataforma', 'email-suporte', 'telefone-suporte', 'idioma', 'timezone'],
    integracao: ['stripe-key', 'whatsapp-api', 'smtp-host', 'smtp-port'],
    seguranca: ['autenticacao-2fa', 'ssl-obrigatorio', 'tentativas-login', 'expirar-sessao'],
  };

  const abaAtualConfigs = configuracoes.filter(c =>
    (configPorAba[abaAtiva] ?? []).includes(c.id)
  );

  const abas = [
    { id: 'geral',      label: 'Geral',       emoji: '⚙️', icon: Globe },
    { id: 'integracao', label: 'Integrações',  emoji: '🔌', icon: Zap },
    { id: 'seguranca',  label: 'Segurança',    emoji: '🔒', icon: Lock },
    { id: 'sistema',    label: 'Sistema',      emoji: '🖥️', icon: Activity },
  ] as const;

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1">Configurações da Plataforma</h2>
            <p className="text-gray-600">Gerencie configurações gerais, integrações, segurança e monitore o sistema</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleBackup} className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-all">
              <Download className="w-5 h-5" /> Backup
            </button>
            {abaAtiva !== 'sistema' && (
              <button onClick={handleSalvar} disabled={!alterado || salvando}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
                  salvo
                    ? 'bg-green-600 text-white'
                    : alterado
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}>
                {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : salvo ? <CheckCircle className="w-5 h-5" /> : <Save className="w-5 h-5" />}
                {salvando ? 'Salvando...' : salvo ? 'Salvo!' : 'Salvar'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {abas.map(aba => (
            <button key={aba.id} onClick={() => setAbaAtiva(aba.id as typeof abaAtiva)}
              className={`flex-1 px-4 py-4 font-bold text-sm transition-colors border-b-2 ${
                abaAtiva === aba.id
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}>
              {aba.emoji} {aba.label}
            </button>
          ))}
        </div>

        <div className="p-6">

          {/* ── ABA SISTEMA ───────────────────────────────────────────────── */}
          {abaAtiva === 'sistema' && (
            <div className="space-y-6">

              {/* Saúde do Sistema */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-black text-gray-900">Saúde do Sistema</h3>
                  <button onClick={fetchHealth} disabled={loadingHealth}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-50">
                    {loadingHealth ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Atualizar
                  </button>
                </div>

                {loadingHealth ? (
                  <div className="flex items-center gap-3 py-8 justify-center">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    <p className="text-gray-500">Verificando saúde do sistema...</p>
                  </div>
                ) : systemHealth ? (
                  <div className="space-y-4">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm border ${
                      systemHealth.status === 'healthy' ? 'bg-green-100 text-green-800 border-green-300' :
                      systemHealth.status === 'degraded' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                      'bg-red-100 text-red-800 border-red-300'
                    }`}>
                      <Activity className="w-4 h-4" />
                      {systemHealth.status === 'healthy' ? '✅ Sistema Saudável' :
                       systemHealth.status === 'degraded' ? '⚠️ Sistema Degradado' : '🔴 Sistema Indisponível'}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2"><Server className="w-4 h-4 text-blue-600" /><p className="text-sm text-gray-600">Uptime</p></div>
                        <p className="text-xl font-black text-gray-900">{Math.floor((systemHealth.uptime ?? 0) / 3600)}h</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-2"><Database className="w-4 h-4 text-green-600" /><p className="text-sm text-gray-600">DB Latência</p></div>
                        <p className="text-xl font-black text-gray-900">{systemHealth.database?.latency ?? 0}ms</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                        <div className="flex items-center gap-2 mb-2"><MemoryStick className="w-4 h-4 text-purple-600" /><p className="text-sm text-gray-600">Memória</p></div>
                        <p className="text-xl font-black text-gray-900">
                          {systemHealth.memory ? `${Math.round(systemHealth.memory.used / 1024 / 1024)}MB` : '—'}
                        </p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                        <div className="flex items-center gap-2 mb-2"><Cpu className="w-4 h-4 text-amber-600" /><p className="text-sm text-gray-600">CPU</p></div>
                        <p className="text-xl font-black text-gray-900">{systemHealth.cpu?.usage ?? 0}%</p>
                      </div>
                    </div>

                    {systemHealth.services?.length > 0 && (
                      <div>
                        <p className="text-sm font-bold text-gray-700 mb-3">Serviços</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {systemHealth.services.map(s => (
                            <div key={s.name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.status === 'up' ? 'bg-green-500' : s.status === 'degraded' ? 'bg-amber-500' : 'bg-red-500'}`} />
                              <span className="text-sm font-semibold text-gray-900">{s.name}</span>
                              <span className={`text-xs font-bold ml-auto ${s.status === 'up' ? 'text-green-600' : s.status === 'degraded' ? 'text-amber-600' : 'text-red-600'}`}>
                                {s.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-semibold">Não foi possível obter status do sistema</p>
                    <button onClick={fetchHealth} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700">Tentar novamente</button>
                  </div>
                )}
              </div>

              {/* Broadcast */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2"><Bell className="w-5 h-5 text-blue-600" /> Broadcast de Notificação</h3>
                {broadcastEnviado && (
                  <div className="mb-4 p-3 bg-green-50 rounded-xl border border-green-200 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-green-800 font-bold text-sm">Notificação enviada com sucesso!</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Título *</label>
                      <input value={broadcast.titulo} onChange={e => setBroadcast(p => ({ ...p, titulo: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold"
                        placeholder="Título da notificação" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Destinatários</label>
                      <select value={broadcast.userType} onChange={e => setBroadcast(p => ({ ...p, userType: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
                        <option value="all">Todos os usuários</option>
                        <option value="affiliate">Filiados</option>
                        <option value="student">Alunos</option>
                        <option value="professional">Profissionais</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Mensagem *</label>
                    <textarea value={broadcast.mensagem} onChange={e => setBroadcast(p => ({ ...p, mensagem: e.target.value }))} rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-gray-900"
                      placeholder="Escreva a mensagem..." />
                  </div>
                  <button onClick={handleBroadcast} disabled={enviandoBroadcast}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center gap-2 disabled:opacity-60">
                    {enviandoBroadcast ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bell className="w-5 h-5" />}
                    Enviar Broadcast
                  </button>
                </div>
              </div>

              {/* Audit Log */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center gap-2"><Shield className="w-5 h-5 text-gray-600" /> Audit Log</h3>
                  <button onClick={fetchAudit} disabled={loadingAudit}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-50">
                    {loadingAudit ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar
                  </button>
                </div>
                {loadingAudit ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  </div>
                ) : auditLog.length === 0 ? (
                  <p className="text-center text-gray-500 py-8 font-semibold">Nenhum registro de auditoria</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-auto">
                    {auditLog.map(entry => (
                      <div key={entry.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">{entry.action} → {entry.entityType}</p>
                          <p className="text-xs text-gray-500">ID: {entry.entityId} · Admin: {entry.adminId}</p>
                        </div>
                        <p className="text-xs text-gray-500 flex-shrink-0">
                          {entry.createdAt ? new Date(entry.createdAt).toLocaleString('pt-BR') : '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Zona de Perigo */}
              <div className="border-t border-gray-200 pt-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-red-900 mb-4">⚠️ Operações Avançadas</h3>
                  <div className="flex flex-wrap gap-3">
                    <button className="px-4 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors">Restaurar Backup</button>
                    <button className="px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors">Limpar Cache</button>
                    <button onClick={() => {
                      if (window.confirm('ATENÇÃO: Isso resetará todas as configurações para o padrão. Confirmar?')) {
                        localStorage.removeItem(SETTINGS_KEY);
                        setConfiguracoes(CONFIG_DEFAULTS);
                        setAlterado(false);
                      }
                    }} className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors">
                      Resetar para Padrão
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── ABAS DE CONFIGURAÇÃO ──────────────────────────────────────── */}
          {abaAtiva !== 'sistema' && (
            <div className="space-y-4">
              {abaAtiva === 'integracao' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-yellow-900">Informações Sensíveis</p>
                    <p className="text-yellow-700 text-sm mt-1">Não compartilhe chaves de API. As configurações são salvas localmente até que o endpoint <code className="bg-yellow-100 px-1 rounded">/admin/settings</code> seja criado no backend.</p>
                  </div>
                </div>
              )}

              {abaAtualConfigs.map(config => (
                <div key={config.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="font-bold text-gray-900">{config.titulo}</label>
                    {config.requerido && <span className="text-red-500 text-sm">*</span>}
                    {config.sensivel && <span className="px-2 py-0.5 rounded text-xs font-bold bg-yellow-100 text-yellow-800 border border-yellow-300">Sensível</span>}
                  </div>
                  <p className="text-gray-500 text-sm mb-4">{config.descricao}</p>

                  {config.tipo === 'toggle' ? (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleAlterar(config.id, !config.valor)}
                        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${config.valor ? 'bg-blue-500' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${config.valor ? 'translate-x-8' : 'translate-x-1'}`} />
                      </button>
                      <span className={`text-sm font-bold ${config.valor ? 'text-blue-600' : 'text-gray-500'}`}>
                        {config.valor ? 'Ativado' : 'Desativado'}
                      </span>
                    </div>
                  ) : config.tipo === 'select' ? (
                    <select value={config.valor as string} onChange={e => handleAlterar(config.id, e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold">
                      {config.opcoes?.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      type={config.tipo === 'email' ? 'email' : config.tipo === 'numero' ? 'number' : 'text'}
                      value={config.valor as string}
                      onChange={e => handleAlterar(config.id, e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold"
                    />
                  )}
                </div>
              ))}

              {alterado && (
                <div className="sticky bottom-4 bg-blue-600 text-white rounded-xl p-4 flex items-center justify-between shadow-xl">
                  <p className="font-bold">Você tem alterações não salvas</p>
                  <button onClick={handleSalvar} disabled={salvando}
                    className="px-6 py-2 bg-white text-blue-600 rounded-lg font-bold hover:bg-blue-50 flex items-center gap-2 disabled:opacity-70">
                    {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {salvando ? 'Salvando...' : 'Salvar Agora'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfiguracoesPlataforma;
